import { ChangeEvent, FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { GetMadaCardResponse, useCheckIsMadaCardMutation } from '@/logic/services';
import { InputAdornment, TextField, useTheme } from '@mui/material';
import {
  getInputCreditCardIcon,
  hashString,
  sliceStringWithoutSpaces,
} from '@/logic/shared';

import InputMask from 'react-input-mask';
import { ValidationCreditCardType } from '@/logic/hooks';
import shallow from 'zustand/shallow';
import { useOrder } from '@/logic/state';
import ImageBoxFuture from '../image-box-future';

interface CreditCardNumberInputProps {
  creditCardType: ValidationCreditCardType;
  creditCardNumber: string;
  isCreditCardNumberValid: boolean;
  handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
  isLoading?: boolean;
}

export const CreditCardNumberInput: FC<CreditCardNumberInputProps> = ({
  creditCardType,
  creditCardNumber,
  isCreditCardNumberValid,
  handleChange,
  isLoading = false,
}) => {
  const theme = useTheme();
  const intl = useIntl();
  const [madaCardMutationResponse, setMadaCardMutationResponse] =
    useState<GetMadaCardResponse>(null);
  const [, setDebouncedTermState] = useState<string>('');
  const checkIsMadaCardMutation = useCheckIsMadaCardMutation();
  const setGuestBookingInfo = useOrder(s => s.setGuestBookingInfo, shallow);

  const handleDebounce = (termState: string) => {
    setDebouncedTermState(prev => {
      if (termState.replace(/ /g, '').length >= 8) {
        if (sliceStringWithoutSpaces(prev) !== sliceStringWithoutSpaces(termState)) {
          checkIsMadaCardMutation.safeMutateAsync(
            {
              data: {
                bh6: hashString(sliceStringWithoutSpaces(termState, 6)),
                bh8: hashString(sliceStringWithoutSpaces(termState)),
              },
            },
            {
              onSuccess: res => {
                setGuestBookingInfo(prevState => ({
                  ...prevState,
                  isMadaCard: res.isMada,
                }));
                setMadaCardMutationResponse(res);
              },
            }
          );
        }
      } else setMadaCardMutationResponse(null);
      return termState;
    });
  };

  return (
    <InputMask
      mask={
        creditCardType === 'american-express'
          ? '9999 999999 99999'
          : '9999 9999 9999 9999'
      }
      maskChar=''
      disabled={isLoading}
      value={creditCardNumber}
      onChange={e => {
        handleChange(e);
        handleDebounce(e.target.value);
      }}
    >
      {() => (
        <TextField
          id='credit-card-number'
          disabled={isLoading}
          label={<FormattedMessage id='my_credit_cards.card_number' />}
          dir='ltr'
          sx={{
            legend: {
              width: 0,
            },
          }}
          InputLabelProps={{
            sx: {
              backgroundColor: 'white',
              paddingInline: '6px',
            },
          }}
          InputProps={{
            sx: {
              color: theme.palette.grey[80],
            },
            startAdornment: (
              <InputAdornment
                position='start'
                sx={{
                  width: '25px',
                  height: '25px',
                }}
              >
                <ImageBoxFuture
                  img={getInputCreditCardIcon(
                    madaCardMutationResponse?.isMada ? 'mada' : creditCardType
                  )}
                  title={intl.formatMessage({
                    id: 'alt.credit_card_icon',
                  })}
                  objectFit='contain'
                  sx={{
                    width: '100%',
                    height: '100%',
                  }}
                />
              </InputAdornment>
            ),
          }}
          inputProps={{
            inputMode: 'numeric',
          }}
          error={!isCreditCardNumberValid && Boolean(creditCardNumber)}
          helperText={
            !isCreditCardNumberValid &&
            Boolean(creditCardNumber) && (
              <FormattedMessage id='my_credit_cards.card_number.invalid' />
            )
          }
        />
      )}
    </InputMask>
  );
};
